import { useEffect, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { toast } from "sonner";

import { canDocflow } from "@/auth/portal";
import { loadDeals } from "@/braining/deals.functions";
import { DEAL_STATUS_LABEL, type DealStatus } from "@/braining/deals";
import type { Deal } from "@/braining/deals-store";
import { NewDeal } from "@/braining/components/new-deal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

/**
 * Комплекты документов с подрядчиками (созвон с Дашей 28.10: «не сделка, а
 * комплект доков»). Как в таблице Даши — два главных фильтра: ID проекта
 * (все подрядчики проекта) и подрядчик (все его проекты); третий —
 * менеджер — учётная запись (у Даши, Тани и маркетинга свой вход; по
 * умолчанию «мои»). Рамочные договоры отдельной
 * строкой не показываем: они в комплектах своих приложений.
 */
export const Route = createFileRoute("/deals")({
  head: () => ({ meta: [{ title: "Комплекты документов — Портал Braining Group" }] }),
  beforeLoad: ({ context }) => {
    if (!canDocflow(context.user?.role)) throw redirect({ to: "/" });
  },
  component: DealsPage,
});

type Filter = "open" | DealStatus | "all";

const OPEN: DealStatus[] = ["draft", "approval", "signing", "signed", "paid", "closing"];

const rub = (n: number | null) =>
  n == null ? "—" : `${n.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;

function DealsPage() {
  const { user } = Route.useRouteContext();
  const [all, setAll] = useState<Deal[] | null>(null);
  const [filter, setFilter] = useState<Filter>("open");
  const [project, setProject] = useState("");
  const [contractor, setContractor] = useState("");
  // У каждого менеджера свой вход (Даша 29.10) — по умолчанию свои комплекты.
  const [manager, setManager] = useState<string>("mine");

  useEffect(() => {
    loadDeals({ data: {} })
      .then(setAll)
      .catch((e: unknown) => {
        console.error("[deals] список не загрузился", e);
        toast.error("Не удалось загрузить комплекты");
      });
  }, []);

  const deals = all?.filter((d) => d.kind !== "frame") ?? null;
  const managers = [
    ...new Set((deals ?? []).map((d) => d.managerEmail).filter((x): x is string => !!x)),
  ];
  const p = project.trim().toLowerCase();
  const c = contractor.trim().toLowerCase();
  const byPeople = (deals ?? []).filter(
    (d) =>
      (!p || d.project.toLowerCase().includes(p)) &&
      (!c || d.contractorName.toLowerCase().includes(c)) &&
      (manager === "all" ||
        (manager === "mine"
          ? d.managerId === user?.id
          : manager === "none"
            ? !d.managerId
            : d.managerEmail === manager)),
  );
  const shown = byPeople.filter(
    (d) => filter === "all" || (filter === "open" ? OPEN.includes(d.status) : d.status === filter),
  );
  const count = (f: Filter) =>
    byPeople.filter((d) =>
      f === "all" ? true : f === "open" ? OPEN.includes(d.status) : d.status === f,
    ).length;

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Комплекты документов</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Договоры, приложения и ДС с подрядчиками: от черновика через согласование у АЛ и подпись
          до закрывающих документов.
        </p>
      </div>

      <NewDeal />

      <Card>
        <CardContent className="space-y-3 py-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input
                value={project}
                placeholder="ID проекта"
                className="h-9 pl-8"
                onChange={(e) => setProject(e.target.value)}
              />
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input
                value={contractor}
                placeholder="Подрядчик"
                className="h-9 pl-8"
                onChange={(e) => setContractor(e.target.value)}
              />
            </div>
            <select
              value={manager}
              onChange={(e) => setManager(e.target.value)}
              className="h-9 rounded-md border bg-background px-2 text-sm"
              aria-label="Менеджер"
            >
              <option value="mine">Мои</option>
              <option value="all">Все менеджеры</option>
              {managers.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value="none">Без менеджера</option>
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {(["open", ...OPEN, "done", "cancelled", "all"] as Filter[]).map((f) => (
              <Button
                key={f}
                size="sm"
                variant={filter === f ? "secondary" : "ghost"}
                className="h-7 px-2.5 text-xs"
                onClick={() => setFilter(f)}
              >
                {f === "open" ? "В работе" : f === "all" ? "Все" : DEAL_STATUS_LABEL[f]}
                <span className="text-muted-foreground">{count(f)}</span>
              </Button>
            ))}
          </div>

          {deals === null ? (
            <p className="text-sm text-muted-foreground">Загружаем…</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {deals.length
                ? "Под фильтр ничего не попало."
                : "Комплектов пока нет — заведите первый выше."}
            </p>
          ) : (
            <div className="divide-y rounded-md border">
              {shown.map((d) => (
                <Link
                  key={d.id}
                  to="/deal/$id"
                  params={{ id: d.id }}
                  className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2.5 text-sm hover:bg-muted/50"
                >
                  <span className="w-28 truncate font-medium">{d.project || "—"}</span>
                  <span className="min-w-0 flex-1 truncate">
                    {d.contractorName}
                    <span className="block truncate text-xs text-muted-foreground">{d.title}</span>
                  </span>
                  <span className="w-28 text-right tabular-nums">{rub(d.amountTotal)}</span>
                  <span className="w-48 text-right text-xs text-muted-foreground">
                    {DEAL_STATUS_LABEL[d.status]}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
